import { Dispatch, ReactElement, SetStateAction, useRef, useState } from "react";
import VoiceRecorderWaveform from "../components/VoiceRecorderWaveform/VoiceRecorderWaveform";
import { AUDIO_CONSTRAINTS, MIME_TYPE } from "../services/voiceRecorderService";
import { RecorderControls, VoiceRecorderFailure, VoiceRecorderStatus, WaveformSession } from "../types/VoiceRecorder";

type UseVoiceRecorderOptions = {
  // Both callbacks fire from browser events, always with the latest render's closures.
  onRecorded: (audio: Blob) => void;
  onFailed: (reason: VoiceRecorderFailure) => void;
};

const isMicrophonePermissionGranted = async (): Promise<boolean> => {
  const permission = await navigator.permissions.query({ name: "microphone" });
  return permission.state === "granted";
};

type RecorderOutcome = { type: "recorded"; audio: Blob } | { type: "failed"; reason: VoiceRecorderFailure };

type SessionPhase = "requestingPermission" | "waitingForLibrary" | "recording" | "finishing" | "ended";

type RecordingSession = WaveformSession & {
  id: number;
  begin: () => void;
  requestStop: () => void;
  cancel: () => void;
};

type SessionWiring = {
  publishStatus: (status: VoiceRecorderStatus) => void;
  publishOutcome: (outcome: RecorderOutcome) => void;
  setRecordingSession: Dispatch<SetStateAction<RecordingSession | null>>;
  setIsWaveformMounted: Dispatch<SetStateAction<boolean>>;
};

let sessionCounter = 0;

// One recording session as a plain object: all mutable per-session state lives in this closure
// instead of hook-level refs. Late library events (the library may invoke stale closures) reach
// the session object they belong to, which knows whether it has ended — no cross-session
// bookkeeping in the hook.
const createRecordingSession = ({
  publishStatus,
  publishOutcome,
  setRecordingSession,
  setIsWaveformMounted,
}: SessionWiring): RecordingSession => {
  const state: { phase: SessionPhase } = { phase: "requestingPermission" };
  // hasEnded must stay a function: cancel() may flip the phase to "ended" during any await in
  // begin(), so a boolean snapshot would go stale — and TS's flow narrowing would even "prove"
  // a direct re-comparison after an earlier check impossible.
  const hasEnded = () => state.phase === "ended";
  let controls: RecorderControls | null = null;
  const chunks: Blob[] = [];

  // Safe as plain resets: each session releases at most once, and the start guard serializes
  // sessions (a new one can only start after this release cleared recordingSession), so a
  // release can never clear a newer session's state. If a second release path or a relaxed
  // start guard is ever introduced, these resets must become identity-checked again.
  const release = () => {
    setRecordingSession(null);
    setIsWaveformMounted(false);
  };

  // The library's getUserMedia call cannot be cancelled and would keep recording if its
  // permission resolves after the session ended. When the permission is not yet granted,
  // settling it with our own throwaway stream first means nothing has started yet when the
  // user cancels or navigates away during the prompt; when it is already granted, no prompt
  // can appear and the preflight is skipped so the microphone is only opened once.
  const begin = async () => {
    if (!(await isMicrophonePermissionGranted())) {
      if (hasEnded()) {
        return;
      }
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
      } catch {
        // Covers denied permission and missing devices.
        if (!hasEnded()) {
          state.phase = "ended";
          release();
          publishOutcome({ type: "failed", reason: "microphone" });
        }
        return;
      }
      // Always release the throwaway stream, even if the session ended while the prompt was open.
      stream.getTracks().forEach((track) => track.stop());
    }
    if (hasEnded()) {
      return;
    }
    // Mounts the waveform element, which starts the actual recording.
    state.phase = "waitingForLibrary";
    setIsWaveformMounted(true);
  };

  const requestStop = () => {
    if (state.phase !== "recording") {
      return;
    }
    state.phase = "finishing";
    publishStatus("finishing");
    controls?.stopRecording();
  };

  const cancel = () => {
    if (hasEnded()) {
      return;
    }
    const waitingForLibrary = state.phase === "waitingForLibrary";
    state.phase = "ended";
    controls?.stopRecording();
    publishStatus("idle");
    if (waitingForLibrary) {
      return;
    }
    release();
  };

  const started = () => {
    if (hasEnded()) {
      // Cancelled while the library's start was pending: the recorder came alive only now,
      // so stop it, release the microphone, and unmount the waveform.
      controls?.stopRecording();
      release();
      return;
    }
    if (state.phase !== "waitingForLibrary") {
      return;
    }
    state.phase = "recording";
    const mediaRecorder = controls?.mediaRecorder;
    // The library requests its own stream without constraints, so pin the backend contract on
    // the live track. A rejection is swallowed: Chrome's defaults match the contract anyway,
    // and the recording must continue either way.
    mediaRecorder?.stream.getAudioTracks().forEach((track) => {
      track.applyConstraints(AUDIO_CONSTRAINTS).catch(() => {});
    });
    // The recorder fires "stop" after flushing the final chunk, so all chunks are complete.
    // The listener is never removed: late or duplicate stop events land in this session
    // after it has ended and are ignored by the phase guards.
    mediaRecorder?.addEventListener("stop", stopped);
    publishStatus("recording");
  };

  const chunk = (blob: Blob) => {
    if (hasEnded()) {
      // Late chunk of a session that was cancelled or already completed: discard.
      return;
    }
    chunks.push(blob);
  };

  // The recorder fires "stop" after flushing the final chunk, so all chunks are complete.
  const stopped = () => {
    const wasRequested = state.phase === "finishing";
    if (!wasRequested && state.phase !== "recording") {
      return;
    }
    state.phase = "ended";
    controls?.stopRecording();
    release();
    if (!wasRequested) {
      // The recording ended without a requested stop, e.g. the microphone was disconnected.
      publishOutcome({ type: "failed", reason: "interrupted" });
      return;
    }
    const audio = new Blob(chunks, { type: MIME_TYPE });
    publishOutcome(audio.size === 0 ? { type: "failed", reason: "recording" } : { type: "recorded", audio });
  };

  const recorderError = () => {
    if (hasEnded()) {
      release();
      return;
    }
    state.phase = "ended";
    controls?.stopRecording();
    release();
    publishOutcome({ type: "failed", reason: "microphone" });
  };

  return {
    id: ++sessionCounter,
    begin,
    requestStop,
    cancel,
    attachControls: (nextControls) => {
      controls = nextControls;
    },
    started,
    chunk,
    recorderError,
  };
};

/**
 * Records microphone audio via react-voice-visualizer behind a simple API:
 * `status`, `start`, `stop`, `cancel`, and a live `waveform` element.
 * Delivers the result exactly once: `onRecorded` with the audio, or `onFailed` with a reason.
 *
 * Consumer contract: render `waveform` whenever it is non-null — it hosts the live recorder
 * (hiding it via CSS is fine). Never key its mounting on `status`.
 */
export const useVoiceRecorder = ({ onRecorded, onFailed }: UseVoiceRecorderOptions) => {
  const [voiceRecorderStatus, setVoiceRecorderStatus] = useState<VoiceRecorderStatus>("idle");
  const [recordingSession, setRecordingSession] = useState<RecordingSession | null>(null);
  const [isWaveformMounted, setIsWaveformMounted] = useState(false);
  // Latest-value publish, idempotent and therefore safe during render: a session outlives the
  // render that started it, so outcomes must reach the newest callbacks, never stale closures.
  const callbacksRef = useRef({ onRecorded, onFailed });
  callbacksRef.current = { onRecorded, onFailed };

  const start = () => {
    if (recordingSession !== null) {
      return;
    }
    const nextSession = createRecordingSession({
      publishStatus: setVoiceRecorderStatus,
      // Returns the recorder to idle and hands the result to the callbacks in the same tick,
      // so both updates commit together.
      publishOutcome: (outcome) => {
        setVoiceRecorderStatus("idle");
        if (outcome.type === "recorded") {
          callbacksRef.current.onRecorded(outcome.audio);
        } else {
          callbacksRef.current.onFailed(outcome.reason);
        }
      },
      setRecordingSession,
      setIsWaveformMounted,
    });
    setRecordingSession(nextSession);
    setVoiceRecorderStatus("requestingPermission");
    nextSession.begin();
  };

  const stop = () => recordingSession?.requestStop();
  const cancel = () => recordingSession?.cancel();

  const waveform: ReactElement | null =
    recordingSession !== null && isWaveformMounted ? (
      <VoiceRecorderWaveform key={recordingSession.id} session={recordingSession} />
    ) : null;

  return { status: voiceRecorderStatus, start, stop, cancel, waveform };
};
